<template>
  <div class="bg-gray-600 mb-2 p-2 pr-0 rounded-8px">
    <div class="grid grid-cols-[72px_19fr_19fr_12fr_96px_5fr] xl:grid-cols-[72px_40fr_36fr_36fr_36fr_22fr_32px] gap-x-4 text-14px leading-5">
      <div class="cover-image w-[72px] h-[72px] rounded-8px overflow-hidden flex items-center justify-center">
        <div v-if="post.phrozen_verified" class="w-5 h-5 flex items-center justify-center absolute top-0 bg-red rounded-br-md text-14px bg-[#E60044]">
          <img src="../../images/home/phrozen_verified.svg" class="w-4 h-4 brightness-[4]" />
        </div>
        <img :src="post.cover_image_src" :alt="post.title" class="max-w-[72px] max-h-[72px]" />
      </div>
      <div class='title flex items-center text-12px xl:text-14px font-medium hover:underline break-word'>
        <a :href="post.visible ? `/posts/${post.id}` : `/posts/${post.id}/preview`" class="">
          {{ post.title }}
        </a>
      </div>
      <div class='created-at flex xl:hidden items-center text-12px xl:text-14px font-medium'>
        {{ formatDate(post.created_at) }}
        <br>
        {{ formatDate(post.updated_at) }}
      </div>
      <div class='created-at hidden xl:flex items-center text-12px xl:text-14px font-medium'>
        {{ formatDate(post.created_at) }}
      </div>
      <div class='updated-at hidden xl:flex items-center text-12px xl:text-14px font-medium'>
        {{ formatDate(post.updated_at) }}
      </div>
      <div class='state flex flex-col justify-center items-center xl:flex-row xl:items-center xl:justify-between'>
        <div class='state-text'>
          <span class="inline-block py-1 px-2 rounded-2xl border "
                    :style="{ color: `var(--color-post-state-${post.state.replace(/_/g, '-')})`,
                          background: `color-mix(in srgb, var(--color-post-state-${post.state.replace(/_/g, '-')}), transparent 80%)`,
                          borderColor: `var(--color-post-state-${post.state.replace(/_/g, '-')})` }"
              >
            {{ $t(`post.state.${post.state}`) }}
          </span>
        </div>
        <a v-if="post.state === 'draft'"
            href="#"
            class="min-w-14 btn btn-primary mt-2 xl:mt-0 xl:ml-4 py-1 px-2 text-12px max-h-6 rounded-4px"
            @click.prevent="submitAction"
            v-bind="isSubmitting ? { disabled: true } : {}">
            {{ isSubmitting ? $t('post.state.pending') : $t('models.index.submit') }}
        </a>
      </div>
      <div class='state-icon flex justify-center items-center'>
        <a :href="`/profile/models/${post.id}/edit`"
            class="edit-btn flex justify-center items-center w-8 h-8 rounded-full
                    filter transition-filter duration-100 ease-in-out
                    hover:[filter:invert(50%)_sepia(10%)_saturate(500%)_hue-rotate(200deg)_brightness(90%)_contrast(90%)]">
          <img src="../../images/edit.svg" class="w-8 h-8 object-contain" />
        </a>
        <a href="#" class="delete-btn flex justify-center items-center w-8 h-8 rounded-full
                            filter transition-filter duration-100 ease-in-out
                            hover:[filter:invert(50%)_sepia(10%)_saturate(500%)_hue-rotate(200deg)_brightness(90%)_contrast(90%)]"
            @click.prevent="confirmDelete">
            <img src="../../images/trash.svg" class="w-8 h-8 object-contain" />
        </a>
      </div>
      <div class='py-2 flex justify-center items-center'>
        <span class="variant-collapse-btn cursor-pointer">
          <div class="w-4 h-4 font-bold text-16px px-4 collapse-btn"
               :class="{ 'checked': variantExpanded }"
               @click="toggleVariant()"></div>
        </span>
      </div>
    </div>
    <div v-if="variantExpanded">
      <hr class='border-grey-3 my-2 mr-2 col-span-6 border-gray-200'>
      <div class="grid
                  grid-cols-[72px_19fr_19fr_12fr_96px_5fr]
                  xl:grid-cols-[72px_40fr_36fr_36fr_36fr_22fr_32px]
                  gap-x-4
                  gap-y-1
                  text-12px text-gray-200">
        <div class=''></div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t(`models.index.catalog`) }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t(`models.index.type`) }}</div>
        <div class='h-5 text-grey-3 font-medium'>{{ $t(`models.base_form.selling_price`) }}</div>
        <div class='h-5 text-grey-3 font-medium '>{{ $t(`models.base_form.original_price`) }}</div>
      </div>
      <div class="grid
                  grid-cols-[72px_19fr_19fr_12fr_96px_5fr]
                  xl:grid-cols-[72px_40fr_36fr_36fr_36fr_22fr_32px]
                  gap-x-4
                  gap-y-1
                  text-12px
                  xl:text-14px">
        <template v-for="variant in post.variants" :key="variant.id">
          <div class="name truncate h-5 col-span-2" :title="variant.name">
            {{ variant.name }}
          </div>
          <div class="file_type h-5">
            {{ variant.file_type }}
          </div>
          <div class="price h-5">
            {{ formatCurrency(variant.price) }}
          </div>
          <div class="original_price h-5 col-span-2 xl:col-span-3">
            {{ formatCurrency(variant.original_price) }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';

export default defineComponent({
  props: {
    post: {
      type: Object,
      required: true
    },
    isLast: {
      type: Boolean,
      default: false
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  emits: ['post-submit', 'post-deleted'],
  setup(props, { emit }) {
    const variantExpanded = ref(false);

    const mayAskForSubmit = computed(() => {
      return props.post.state === 'draft' || props.post.state === 'submit_rejection'
    })

    // 提供一个方法用于外部控制展开状态
    const toggleVariant = () => {
      variantExpanded.value = !variantExpanded.value;
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const submitAction = async () => {
      if (props.isSubmitting) return;
      emit('post-submit', props.post.id, props.post.state);
    };

    const confirmDelete = async () => {
      if (confirm("Are you sure you want to delete this post?")) {
        try {
          const response = await fetch(`/profile/models/${props.post.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
            }
          });

          const result = await response.json();

          if (response.ok && result.success) {
            emit('post-deleted', props.post);
          } else {
            throw new Error(result.message || 'Failed to delete post');
          }
        } catch (error) {
          console.error('Error deleting post:', error);
          alert(error.message);
        }
      }
    };

    return {
      mayAskForSubmit,
      variantExpanded,
      formatDate,
      formatCurrency,
      submitAction,
      confirmDelete,
      toggleVariant
    };
  }
});
</script>
